import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../auth';
import { fetchMigrationLogsFromService } from '../../services/Migration/fetchMigrationLogsService';
import { logger } from '../../utils/logger';
import type { MigrationLogEntry, LogFilters } from '../../types/logs';
import { DEFAULT_LOG_FILTERS } from '../../types/logs';

interface UseFetchMigrationLogsOptions {
  /** ID of the migration to fetch logs for */
  migrationId: string;
  /** Initial filter state */
  initialFilters?: Partial<LogFilters>;
  /** Whether to fetch logs automatically on mount */
  autoFetch?: boolean;
}

interface UseFetchMigrationLogsReturn {
  /** Array of log entries */
  logs: MigrationLogEntry[];
  /** Total count of logs (across all pages) */
  totalCount: number;
  /** Migration name from the response */
  migrationName: string;
  /** Whether logs are currently being fetched */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Current filter state */
  filters: LogFilters;
  /** Update filters and trigger a new fetch */
  updateFilters: (newFilters: Partial<LogFilters>) => void;
  /** Manually trigger a logs fetch */
  fetchLogs: () => Promise<void>;
}

/**
 * Hook to fetch and manage migration logs with filtering and pagination
 */
export const useFetchMigrationLogs = ({
  migrationId,
  initialFilters,
  autoFetch = true
}: UseFetchMigrationLogsOptions): UseFetchMigrationLogsReturn => {
  const { accessToken } = useAuth();
  const [logs, setLogs] = useState<MigrationLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [migrationName, setMigrationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LogFilters>({
    ...DEFAULT_LOG_FILTERS,
    ...initialFilters
  } as LogFilters);

  const fetchLogs = useCallback(async () => {
    if (!accessToken) {
      setError('No access token available');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchMigrationLogsFromService(accessToken, migrationId, filters);

      setLogs(response.logs);
      setTotalCount(response.totalCount);
      setMigrationName(response.migrationName);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch logs';
      setError(errorMessage);
      logger.error('Error fetching migration logs in hook', { err, migrationId });
    } finally {
      setLoading(false);
    }
  }, [accessToken, migrationId, filters]);

  const updateFilters = useCallback((newFilters: Partial<LogFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      // Reset to page 1 when filters change (except when only page changes)
      page: newFilters.page !== undefined ? newFilters.page : 1
    }));
  }, []);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && accessToken) {
      fetchLogs();
    }
  }, [autoFetch, accessToken, fetchLogs]);

  return {
    logs,
    totalCount,
    migrationName,
    loading,
    error,
    filters,
    updateFilters,
    fetchLogs
  };
};
